import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Modal, Button, Select, Input, Field, Spinner } from "../ui";
import { Plus, Trash2, X, Zap, Power } from "lucide-react";
import { cn } from "../../lib/utils";

const TRIGGERS = ["status_change", "item_created", "due_overdue"];
const ACTIONS = ["notify_owner", "move_group", "set_value", "complete_subitems"];
const OPTION_TYPES = ["status", "priority", "dropdown"];

// Build a readable one-line summary of a rule, e.g. "When Status → Done · notify owner, move to Done".
function summarize(rule, board, t) {
  const trig = rule.trigger || {};
  const col = (id) => (board.columns.find((c) => c.id === id)?.label) || id;
  const opt = (cid, oid) => {
    const c = board.columns.find((x) => x.id === cid);
    return c?.settings?.options?.find((o) => o.id === oid)?.label || oid;
  };
  let tr = t("autom_when");
  if (trig.type === "status_change") tr += ` ${col(trig.col_id)} → ${opt(trig.col_id, trig.to)}`;
  else if (trig.type === "item_created") tr += ` ${t("autom_trig_item_created")}`;
  else if (trig.type === "due_overdue") tr += ` ${col(trig.col_id)} ${t("autom_trig_overdue_short")}`;
  const acts = (rule.actions || []).map((a) => {
    if (a.type === "notify_owner") return t("autom_act_notify_owner");
    if (a.type === "move_group") return `${t("autom_act_move_to")} ${board.groups.find((g) => g.id === a.group_id)?.name || "?"}`;
    if (a.type === "set_value") return `${t("autom_act_set")} ${col(a.col_id)}`;
    if (a.type === "complete_subitems") return t("autom_act_complete_subitems");
    return a.type;
  });
  return `${tr} · ${acts.join(", ")}`;
}

function emptyRule(board) {
  const status = board.columns.find((c) => c.type === "status");
  const done = status?.settings?.options?.find((o) => o.done)?.id || status?.settings?.options?.[0]?.id;
  return {
    name: "",
    enabled: true,
    trigger: { type: "status_change", col_id: status?.id, to: done },
    actions: [{ type: "notify_owner" }],
  };
}

export default function AutomationsModal({ board, members = [], onClose }) {
  const { t, toast } = useApp();
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState(null); // building/editing a rule
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const statusCols = board.columns.filter((c) => c.type === "status");
  const dateCols = board.columns.filter((c) => c.type === "date" || c.type === "timeline");

  // one-click common rules ("quick add" gallery), computed from this board's schema
  const gallery = (() => {
    const sc = statusCols[0];
    const doneOpt = sc?.settings?.options?.find((o) => o.done)?.id;
    const stuckOpt = sc?.settings?.options?.find((o) => /stuck|bloq/i.test(o.id) || /stuck|bloq/i.test(o.label || ""))?.id;
    const dateCol = dateCols[0]?.id;
    const doneGroup = board.groups.find((g) => /done|termin/i.test(g.name || ""))?.id || board.groups[board.groups.length - 1]?.id;
    const subHasDone = (board.subitem_columns || []).find((c) => c.type === "status")?.settings?.options?.some((o) => o.done);
    const g = [];
    if (sc && doneOpt) g.push({ key: "notify-done", label: t("autom_tpl_notify_done"),
      payload: { name: t("autom_tpl_notify_done"), trigger: { type: "status_change", col_id: sc.id, to: doneOpt }, actions: [{ type: "notify_owner" }] } });
    if (sc && doneOpt && doneGroup) g.push({ key: "move-done", label: t("autom_tpl_move_done"),
      payload: { name: t("autom_tpl_move_done"), trigger: { type: "status_change", col_id: sc.id, to: doneOpt }, actions: [{ type: "move_group", group_id: doneGroup }] } });
    if (sc && doneOpt && subHasDone) g.push({ key: "complete-subs", label: t("autom_tpl_complete_subs"),
      payload: { name: t("autom_tpl_complete_subs"), trigger: { type: "status_change", col_id: sc.id, to: doneOpt }, actions: [{ type: "complete_subitems" }] } });
    if (sc && dateCol) g.push({ key: "overdue", label: t("autom_tpl_overdue"),
      payload: { name: t("autom_tpl_overdue"), trigger: { type: "due_overdue", col_id: dateCol },
        actions: stuckOpt ? [{ type: "notify_owner" }, { type: "set_value", col_id: sc.id, value: stuckOpt }] : [{ type: "notify_owner" }] } });
    return g;
  })();

  const addFromTemplate = async (payload) => {
    setBusy(true);
    try { await api.post(`/boards/${board.id}/automations`, payload); toast(t("autom_saved")); load(); }
    catch (e) { toast(e?.response?.data?.detail || t("autom_save_failed")); }
    finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/boards/${board.id}/automations`);
      setRules(data);
    } catch { setRules([]); }
  }, [board.id]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditId(null); setDraft(emptyRule(board)); };
  const startEdit = (r) => { setEditId(r.id); setDraft(JSON.parse(JSON.stringify(r))); };

  const setTriggerType = (type) => setDraft((d) => {
    const next = { ...d, trigger: { type } };
    if (type === "status_change") {
      const c = statusCols[0];
      next.trigger.col_id = c?.id;
      next.trigger.to = c?.settings?.options?.find((o) => o.done)?.id || c?.settings?.options?.[0]?.id;
    } else if (type === "due_overdue") {
      next.trigger.col_id = dateCols[0]?.id;
    }
    return next;
  });

  const optionsFor = (colId) => board.columns.find((c) => c.id === colId)?.settings?.options || [];

  const addAction = () => setDraft((d) => ({ ...d, actions: [...d.actions, { type: "notify_owner" }] }));
  const removeAction = (i) => setDraft((d) => ({ ...d, actions: d.actions.filter((_, idx) => idx !== i) }));
  const setAction = (i, patch) => setDraft((d) => ({ ...d, actions: d.actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) }));
  const setActionType = (i, type) => {
    const seed = {};
    if (type === "move_group") seed.group_id = board.groups[0]?.id;
    if (type === "set_value") { seed.col_id = board.columns[0]?.id; seed.value = ""; }
    setAction(i, { type, ...seed });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: draft.name?.trim() || null, enabled: draft.enabled,
        trigger: draft.trigger, actions: draft.actions };
      if (editId) await api.put(`/board-automations/${editId}`, payload);
      else await api.post(`/boards/${board.id}/automations`, payload);
      setDraft(null); setEditId(null);
      toast(t("autom_saved"));
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || t("autom_save_failed"));
    } finally { setBusy(false); }
  };

  const toggleEnabled = async (r) => {
    await api.put(`/board-automations/${r.id}`, { name: r.name, enabled: !r.enabled, trigger: r.trigger, actions: r.actions });
    load();
  };
  const del = async (r) => { await api.delete(`/board-automations/${r.id}`); toast(t("autom_deleted")); load(); };

  const emptyVal = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0);
  const draftInvalid = !!draft && (
    (draft.trigger.type !== "item_created" && !draft.trigger.col_id) ||
    !draft.actions.length ||
    draft.actions.some((a) =>
      (a.type === "set_value" && (!a.col_id || emptyVal(a.value))) ||
      (a.type === "move_group" && !a.group_id))
  );

  // ---- value editor for a set_value action ----
  const ValueEditor = ({ action, idx }) => {
    const col = board.columns.find((c) => c.id === action.col_id);
    if (!col) return null;
    if (OPTION_TYPES.includes(col.type)) {
      return (
        <Select value={action.value || ""} onChange={(e) => setAction(idx, { value: e.target.value })} data-testid="autom-setvalue-option">
          <option value="">—</option>
          {(col.settings?.options || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </Select>
      );
    }
    if (col.type === "people") {
      const ids = Array.isArray(action.value) ? action.value : [];
      const toggle = (id) => setAction(idx, { value: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
      return (
        <div className="flex flex-wrap gap-1.5" data-testid="autom-setvalue-people">
          {members.map((m) => (
            <button key={m.user_id} onClick={() => toggle(m.user_id)}
              className={cn("px-2 py-1 rounded-full border text-xs", ids.includes(m.user_id) ? "border-primary bg-primary/10 text-text-primary" : "text-text-secondary")}>
              {m.name || m.email}
            </button>
          ))}
          {!members.length && <span className="text-xs text-text-secondary">{t("board_no_members")}</span>}
        </div>
      );
    }
    const type = col.type === "number" ? "number" : col.type === "date" ? "date" : "text";
    return <Input type={type} value={action.value || ""} onChange={(e) => setAction(idx, { value: e.target.value })} data-testid="autom-setvalue-input" />;
  };

  return (
    <Modal open onClose={onClose} title={<span className="inline-flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> {t("autom_title")}</span>}
      footer={draft ? (
        <>
          <Button variant="outline" onClick={() => { setDraft(null); setEditId(null); }} data-testid="autom-cancel">{t("cancel")}</Button>
          <Button onClick={save} disabled={busy || draftInvalid} data-testid="autom-save">{busy ? t("loading") : t("save")}</Button>
        </>
      ) : (
        <Button onClick={onClose}>{t("done") || "Done"}</Button>
      )}>
      {rules === null ? (
        <div className="py-8 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>
      ) : draft ? (
        <div className="space-y-4" data-testid="autom-builder">
          <Field label={t("autom_name_optional")}>
            <Input value={draft.name || ""} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder={t("autom_name_placeholder")} data-testid="autom-name" />
          </Field>

          <div className="rounded-xl border p-3 bg-surface">
            <div className="text-[11px] uppercase tracking-wide text-text-secondary mb-2">{t("autom_when")}</div>
            <Select value={draft.trigger.type} onChange={(e) => setTriggerType(e.target.value)} data-testid="autom-trigger-type">
              {TRIGGERS.map((tt) => <option key={tt} value={tt}>{t(`autom_trig_${tt}`)}</option>)}
            </Select>
            {draft.trigger.type === "status_change" && (
              <div className="flex gap-2 mt-2">
                <Select value={draft.trigger.col_id || ""} onChange={(e) => setDraft((d) => ({ ...d, trigger: { ...d.trigger, col_id: e.target.value, to: optionsFor(e.target.value)[0]?.id } }))} data-testid="autom-trigger-col">
                  {statusCols.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </Select>
                <Select value={draft.trigger.to || ""} onChange={(e) => setDraft((d) => ({ ...d, trigger: { ...d.trigger, to: e.target.value } }))} data-testid="autom-trigger-to">
                  {optionsFor(draft.trigger.col_id).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </Select>
              </div>
            )}
            {draft.trigger.type === "due_overdue" && (
              <Select value={draft.trigger.col_id || ""} onChange={(e) => setDraft((d) => ({ ...d, trigger: { ...d.trigger, col_id: e.target.value } }))} className="mt-2" data-testid="autom-trigger-datecol">
                {dateCols.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                {!dateCols.length && <option value="">—</option>}
              </Select>
            )}
          </div>

          <div className="rounded-xl border p-3 bg-surface">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wide text-text-secondary">{t("autom_then")}</div>
              <button onClick={addAction} data-testid="autom-add-action" className="text-xs text-primary inline-flex items-center gap-1 hover:underline"><Plus className="w-3.5 h-3.5" />{t("autom_add_action")}</button>
            </div>
            <div className="space-y-2">
              {draft.actions.map((a, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`autom-action-${i}`}>
                  <Select value={a.type} onChange={(e) => setActionType(i, e.target.value)} className="max-w-[180px]" data-testid={`autom-action-type-${i}`}>
                    {ACTIONS.map((at) => <option key={at} value={at}>{t(`autom_act_${at}`)}</option>)}
                  </Select>
                  <div className="flex-1 min-w-0">
                    {a.type === "move_group" && (
                      <Select value={a.group_id || ""} onChange={(e) => setAction(i, { group_id: e.target.value })} data-testid={`autom-action-group-${i}`}>
                        {board.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </Select>
                    )}
                    {a.type === "set_value" && (
                      <div className="flex gap-2">
                        <Select value={a.col_id || ""} onChange={(e) => setAction(i, { col_id: e.target.value, value: "" })} className="max-w-[150px]" data-testid={`autom-action-col-${i}`}>
                          {board.columns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                        </Select>
                        <div className="flex-1"><ValueEditor action={a} idx={i} /></div>
                      </div>
                    )}
                  </div>
                  {draft.actions.length > 1 && (
                    <button onClick={() => removeAction(i)} data-testid={`autom-remove-action-${i}`} className="text-text-secondary hover:text-red-400 mt-2"><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="autom-list">
          {rules.length === 0 && (
            <div className="text-center py-8">
              <Zap className="w-8 h-8 text-text-secondary/40 mx-auto mb-2" />
              <p className="text-sm text-text-secondary">{t("autom_empty")}</p>
            </div>
          )}
          {rules.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border" data-testid={`autom-rule-${r.id}`}>
              <button onClick={() => toggleEnabled(r)} title={r.enabled ? t("autom_enabled") : t("autom_disabled")}
                data-testid={`autom-toggle-${r.id}`}
                className={cn("shrink-0 transition-colors", r.enabled ? "text-emerald-400" : "text-text-secondary/50")}>
                <Power className="w-4 h-4" />
              </button>
              <div className="min-w-0 flex-1">
                {r.name && <div className="text-sm font-medium truncate">{r.name}</div>}
                <div className={cn("text-xs truncate", r.enabled ? "text-text-secondary" : "text-text-secondary/50")}>{summarize(r, board, t)}</div>
              </div>
              <button onClick={() => startEdit(r)} data-testid={`autom-edit-${r.id}`} className="text-xs text-text-secondary hover:text-text-primary">{t("edit")}</button>
              <button onClick={() => del(r)} data-testid={`autom-delete-${r.id}`} className="text-text-secondary hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {gallery.length > 0 && (
            <div className="pt-1" data-testid="autom-gallery">
              <div className="text-[11px] uppercase tracking-wide text-text-secondary mb-2">{t("autom_quick_add")}</div>
              <div className="flex flex-wrap gap-2">
                {gallery.map((g) => (
                  <button key={g.key} onClick={() => addFromTemplate(g.payload)} disabled={busy} data-testid={`autom-tpl-${g.key}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs text-text-secondary hover:text-text-primary hover:border-primary transition-colors disabled:opacity-50">
                    <Plus className="w-3.5 h-3.5" />{g.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Button variant="outline" className="w-full" onClick={startNew} data-testid="autom-new"><Plus className="w-4 h-4 mr-1.5" />{t("autom_new_rule")}</Button>
        </div>
      )}
    </Modal>
  );
}
